<script setup lang="ts">
import Draggable from "vuedraggable";
import type { VideoCallParticipant, VideoCallRoom } from "~/types/videoCalls";

const props = defineProps<{
  room: VideoCallRoom;
  rooms: VideoCallRoom[];
  participants: VideoCallParticipant[];
  helpParticipantIds: string[];
  currentRoomId: string;
  prepared: boolean;
  disabled: boolean;
  movingParticipantId: string | null;
}>();

const emit = defineEmits<{
  rename: [name: string];
  remove: [];
  enter: [];
  move: [participantId: string, roomId: string];
}>();

const nameInputId = useId();
const roomName = ref(props.room.name);
const dragParticipants = ref([...props.participants]);
const hasTeacher = computed(() =>
  props.participants.some((participant) => participant.role === "host"),
);
const helpCount = computed(() => props.helpParticipantIds.length);

watch([() => props.participants, () => props.disabled], () => {
  dragParticipants.value = [...props.participants];
  if (!props.disabled) roomName.value = props.room.name;
});
watch(
  () => props.room.name,
  (name) => {
    roomName.value = name;
  },
);

const rename = (): void => {
  const name = roomName.value.trim();
  if (!name || name === props.room.name) {
    roomName.value = props.room.name;
    return;
  }
  emit("rename", name);
};

const onDragChange = (change: {
  added?: { element: VideoCallParticipant };
}): void => {
  if (change.added && !props.disabled) {
    emit("move", change.added.element.id, props.room.id);
  }
  // The server confirms the move. A failed request keeps the original card.
  nextTick(() => {
    dragParticipants.value = [...props.participants];
  });
};

const selectRoom = (participantId: string, event: Event): void => {
  const select = event.target as HTMLSelectElement;
  const roomId = select.value;
  select.value = props.room.id;
  if (roomId !== props.room.id) emit("move", participantId, roomId);
};
</script>

<template>
  <section
    class="room-card"
    :class="{
      'room-card--current': props.currentRoomId === props.room.id,
      'room-card--help': helpCount,
    }"
    :aria-label="props.room.name"
    :data-room-id="props.room.id"
  >
    <header class="room-card__header">
      <div class="room-card__chips">
        <span class="room-card__chip">
          {{ props.participants.length }}
          {{ props.participants.length === 1 ? "pessoa" : "pessoas" }}
        </span>
        <span
          v-if="hasTeacher"
          class="room-card__chip room-card__chip--teacher"
        >
          <i class="mdi mdi-school-outline" aria-hidden="true" /> Professor
        </span>
        <span v-if="helpCount" class="room-card__chip room-card__chip--help">
          <i class="mdi mdi-hand-wave-outline" aria-hidden="true" />
          {{ helpCount }} {{ helpCount === 1 ? "chamado" : "chamados" }}
        </span>
      </div>
      <div class="room-card__title-row">
        <h3 v-if="props.room.is_main">{{ props.room.name }}</h3>
        <template v-else>
          <label :for="nameInputId" class="room-card__sr-only">
            Renomear {{ props.room.name }}
          </label>
          <input
            :id="nameInputId"
            v-model="roomName"
            class="room-card__name"
            type="text"
            maxlength="80"
            :disabled="props.disabled"
            @blur="rename"
            @keydown.enter.prevent="($event.target as HTMLInputElement).blur()"
            @keydown.esc="roomName = props.room.name"
          />
          <button
            v-if="!props.participants.length"
            type="button"
            class="room-card__remove"
            :aria-label="`Excluir ${props.room.name}`"
            :disabled="props.disabled"
            @click="emit('remove')"
          >
            <i class="mdi mdi-trash-can-outline" aria-hidden="true" />
          </button>
        </template>
      </div>
    </header>

    <Draggable
      v-model="dragParticipants"
      item-key="id"
      group="video-call-rooms"
      handle=".room-card__drag-handle"
      :sort="false"
      :disabled="props.disabled"
      :animation="140"
      ghost-class="room-card__participant--ghost"
      class="room-card__participants"
      :aria-label="`Participantes de ${props.room.name}`"
      @change="onDragChange"
    >
      <template #item="{ element: participant }">
        <div
          class="room-card__participant"
          :class="{
            'room-card__participant--moving':
              props.movingParticipantId === participant.id,
          }"
          :data-participant-id="participant.id"
        >
          <div class="room-card__participant-heading">
            <span
              v-if="participant.role !== 'host'"
              class="room-card__drag-handle"
              :class="{ 'room-card__drag-handle--disabled': props.disabled }"
              aria-hidden="true"
            >
              <i class="mdi mdi-drag" />
            </span>
            <i v-else class="mdi mdi-school-outline" aria-hidden="true" />
            <span class="room-card__participant-name">{{
              participant.name
            }}</span>
            <i
              v-if="props.helpParticipantIds.includes(participant.id)"
              class="mdi mdi-hand-wave-outline room-card__help-icon"
              aria-label="Chamou o professor"
              role="img"
            />
          </div>
          <span class="room-card__connection">
            <span
              class="room-card__connection-dot"
              :class="{
                'room-card__connection-dot--connected':
                  participant.is_connected,
              }"
              aria-hidden="true"
            />
            {{
              props.movingParticipantId === participant.id
                ? "Movendo…"
                : participant.is_connected
                  ? "Conectado"
                  : "Reconectando…"
            }}
          </span>
          <label v-if="participant.role !== 'host'" class="room-card__move">
            <span class="room-card__sr-only"
              >Mover {{ participant.name }} para</span
            >
            <select
              :value="props.room.id"
              :disabled="props.disabled"
              @change="selectRoom(participant.id, $event)"
            >
              <option :value="props.room.id" disabled>Mover para…</option>
              <option
                v-for="destination in props.rooms.filter(
                  (room) => room.id !== props.room.id,
                )"
                :key="destination.id"
                :value="destination.id"
              >
                {{ destination.name }}
              </option>
            </select>
          </label>
        </div>
      </template>
      <template #footer>
        <p v-if="!dragParticipants.length" class="room-card__empty">
          <i class="mdi mdi-account-plus-outline" aria-hidden="true" />
          Arraste um aluno para cá
        </p>
      </template>
    </Draggable>

    <footer v-if="!props.prepared" class="room-card__footer">
      <span
        v-if="props.currentRoomId === props.room.id"
        class="room-card__here"
      >
        <i class="mdi mdi-check-circle-outline" aria-hidden="true" />
        Você está aqui
      </span>
      <button
        v-else
        type="button"
        class="room-card__enter"
        :disabled="props.disabled"
        :aria-label="`Entrar em ${props.room.name}`"
        @click="emit('enter')"
      >
        <i class="mdi mdi-login" aria-hidden="true" /> Entrar na sala
      </button>
    </footer>
  </section>
</template>

<style scoped lang="scss">
.room-card {
  display: flex;
  min-width: 0;
  flex-direction: column;
  border: 1px solid #dbe1ef;
  border-radius: 12px;
  background: #fff;
  color: #26334d;
}

.room-card--current {
  border-color: #7d92ea;
  box-shadow: 0 0 0 1px #7d92ea;
}

.room-card--help {
  border-color: #d29739;
}

.room-card__header {
  padding: 14px 14px 10px;
  border-bottom: 1px solid #edf0f6;
}

.room-card__chips {
  display: flex;
  min-height: 23px;
  flex-wrap: wrap;
  gap: 5px;
  margin-bottom: 8px;
}

.room-card__chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 6px;
  border-radius: 5px;
  background: #f0f3f9;
  color: #53617c;
  font-size: 0.68rem;
  font-weight: 700;
  line-height: 1.3;
}

.room-card__chip--teacher {
  background: #edf1ff;
  color: #3153ba;
}

.room-card__chip--help {
  background: #fff3dc;
  color: #805000;
}

.room-card__title-row {
  display: flex;
  min-height: 36px;
  align-items: center;
  gap: 4px;

  h3 {
    font-size: 0.95rem;
    font-weight: 700;
  }
}

.room-card__name {
  width: 100%;
  min-width: 0;
  padding: 7px 5px;
  border: 1px solid transparent;
  border-radius: 6px;
  color: inherit;
  font: inherit;
  font-size: 0.95rem;
  font-weight: 700;

  &:hover:not(:disabled),
  &:focus-visible {
    border-color: #b9c6e0;
    background: #f8faff;
  }
}

.room-card__remove {
  display: grid;
  width: 32px;
  height: 36px;
  flex: 0 0 32px;
  padding: 0;
  place-items: center;
  border-radius: 6px;
  color: #68768c;
  cursor: pointer;
}

.room-card__participants {
  display: grid;
  min-height: 120px;
  align-content: start;
  flex: 1;
  gap: 8px;
  padding: 12px;
}

.room-card__participant {
  padding: 10px;
  border: 1px solid #e4e9f3;
  border-radius: 8px;
  background: #f8faff;
}

.room-card__participant--ghost {
  opacity: 0.35;
}

.room-card__participant--moving {
  border-color: #7d92ea;
}

.room-card__participant-heading {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.82rem;
  font-weight: 700;
}

.room-card__participant-name {
  min-width: 0;
  flex: 1;
  overflow-wrap: anywhere;
}

.room-card__drag-handle {
  display: grid;
  min-width: 24px;
  height: 28px;
  place-items: center;
  color: #75829a;
  cursor: grab;
  touch-action: none;
}

.room-card__drag-handle--disabled {
  cursor: default;
}

.room-card__help-icon {
  color: #9a6309;
}

.room-card__connection {
  display: flex;
  align-items: center;
  gap: 5px;
  margin: 4px 0 8px;
  color: #66748d;
  font-size: 0.69rem;
}

.room-card__connection-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #c19134;
}

.room-card__connection-dot--connected {
  background: #25836b;
}

.room-card__move select {
  width: 100%;
  min-height: 36px;
  padding: 5px 8px;
  border: 1px solid #d6deed;
  border-radius: 6px;
  background: #fff;
  color: #3d527e;
  font: inherit;
  font-size: 0.75rem;
  cursor: pointer;
}

.room-card__empty {
  display: grid;
  min-height: 88px;
  align-content: center;
  justify-items: center;
  gap: 6px;
  border: 1px dashed #d6deed;
  border-radius: 8px;
  color: #75829a;
  font-size: 0.75rem;
  text-align: center;

  i {
    font-size: 1.4rem;
  }
}

.room-card__footer {
  padding: 0 12px 12px;
}

.room-card__enter,
.room-card__here {
  display: flex;
  min-height: 38px;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 7px 10px;
  border: 1px solid #ccd7f0;
  border-radius: 8px;
  color: #3153ba;
  font: inherit;
  font-size: 0.76rem;
  font-weight: 700;
  cursor: pointer;
}

.room-card__here {
  border-color: transparent;
  background: #edf1ff;
  cursor: default;
}

button:disabled,
select:disabled,
input:disabled {
  cursor: default;
  opacity: 0.6;
}

button:focus-visible,
select:focus-visible,
input:focus-visible {
  outline: 2px solid #3153ba;
  outline-offset: 2px;
}

.room-card__sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
}
</style>
